import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input, Card } from '@/components/common';

const experienceSchema = z.object({
  company: z.string().min(1, 'Company name is required'),
  position: z.string().min(1, 'Position is required'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  description: z.string().optional(),
  current: z.boolean().optional(),
});

export type ExperienceFormData = z.infer<typeof experienceSchema>;

export interface Experience extends Omit<ExperienceFormData, 'current'> {
  id?: string;
}

interface ExperienceEditorProps {
  experiences: Experience[];
  onChange: (experiences: Experience[]) => void;
  disabled?: boolean;
}

export function ExperienceEditor({
  experiences,
  onChange,
  disabled = false,
}: ExperienceEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<ExperienceFormData>({
    resolver: zodResolver(experienceSchema),
  });

  const isCurrent = watch('current');

  const handleAdd = () => {
    setIsAdding(true);
    setEditingIndex(null);
    reset({
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      description: '',
      current: false,
    });
  };

  const handleEdit = (index: number) => {
    const exp = experiences[index];
    setEditingIndex(index);
    setIsAdding(false);
    reset({
      ...exp,
      current: !exp.endDate,
    });
  };

  const handleDelete = (index: number) => {
    const updated = experiences.filter((_, i) => i !== index);
    onChange(updated);
  };

  const onSubmit = (data: ExperienceFormData) => {
    const { current, ...expData } = data;
    const experience: Experience = {
      ...expData,
      endDate: current ? undefined : expData.endDate,
    };

    if (editingIndex !== null) {
      // Update existing
      const updated = [...experiences];
      updated[editingIndex] = { ...updated[editingIndex], ...experience };
      onChange(updated);
    } else {
      // Add new
      onChange([...experiences, experience]);
    }

    setIsAdding(false);
    setEditingIndex(null);
    reset();
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingIndex(null);
    reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Work Experience</h3>
        {!isAdding && editingIndex === null && (
          <Button size="sm" onClick={handleAdd} disabled={disabled}>
            + Add Experience
          </Button>
        )}
      </div>

      {/* Experience List */}
      {!isAdding && editingIndex === null && (
        <div className="space-y-3">
          {experiences.length === 0 ? (
            <p className="text-text-muted text-sm">No work experience added yet</p>
          ) : (
            experiences.map((exp, index) => (
              <Card key={index} className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-text-primary">{exp.position}</h4>
                    <p className="text-text-secondary">{exp.company}</p>
                    <p className="text-sm text-text-muted">
                      {exp.startDate} - {exp.endDate || 'Present'}
                    </p>
                    {exp.description && (
                      <p className="mt-2 text-sm text-text-secondary">{exp.description}</p>
                    )}
                  </div>
                  <div className="flex gap-1 ml-4 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(index)}
                      disabled={disabled}
                      className="text-primary hover:text-primary-dark text-sm px-2 py-1.5 rounded hover:bg-primary-soft min-h-[44px] flex items-center"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      disabled={disabled}
                      className="text-red-600 hover:text-red-800 text-sm px-2 py-1.5 rounded hover:bg-error-soft min-h-[44px] flex items-center"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Form */}
      {(isAdding || editingIndex !== null) && (
        <Card className="p-4">
          <div className="space-y-4">
            <h4 className="font-semibold text-text-primary">
              {editingIndex !== null ? 'Edit Experience' : 'Add Experience'}
            </h4>

            <Input
              label="Company *"
              error={errors.company?.message}
              fullWidth
              {...register('company')}
            />

            <Input
              label="Position *"
              error={errors.position?.message}
              fullWidth
              {...register('position')}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Start Date *"
                type="month"
                error={errors.startDate?.message}
                fullWidth
                {...register('startDate')}
              />
              <Input
                label="End Date"
                type="month"
                error={errors.endDate?.message}
                fullWidth
                disabled={isCurrent}
                {...register('endDate')}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="current"
                className="mr-2"
                {...register('current')}
                onChange={(e) => {
                  setValue('current', e.target.checked);
                  if (e.target.checked) {
                    setValue('endDate', '');
                  }
                }}
              />
              <label htmlFor="current" className="text-sm text-text-secondary">
                I currently work here
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">Description</label>
              <textarea
                rows={4}
                placeholder="Describe your responsibilities and achievements..."
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                {...register('description')}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSubmit(onSubmit)}>
                {editingIndex !== null ? 'Update' : 'Add'} Experience
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
